/**
 * Aritmetica de Facturacion Global V4 (CRM-3108).
 *
 * El backend entrega el estado inicial ya calculado; en cuanto el usuario
 * marca la casilla Manual de un renglon, el front recalcula toda la cadena:
 *
 *   payment  -> subtotal = PU * litros, total = subtotal * (1 + tasa)
 *   tasa     -> suma de sus payments; el PU es el promedio simple de ellos
 *   permiso  -> suma de sus tasas
 *   pie      -> Total Acumulado = suma de los permisos
 *
 * Son funciones puras, sin store ni red, para poder probarlas solas.
 */

import { isInvalidQuantity, MIN_INVOICE_QUANTITY } from "./validateQuantity";

export const PERCENT = 100;

/**
 * El modulo trunca a dos decimales en vez de redondear. Es la convencion que
 * ya traia el reducer y se conserva para no mover importes existentes.
 */
export const truncateAmount = (value) =>
  Math.trunc(Math.round((Number(value) || 0) * PERCENT * 1e6) / 1e6) / PERCENT;

/**
 * Limites de PU y litros capturados en Manual. Unica fuente de verdad: antes
 * el reducer los aplicaba solo, y el input podia mostrar un valor distinto
 * del que de verdad se timbraba (ej. capturar 1500 mostraba "1500" pero se
 * timbraba 999.99). Ahora el componente usa el mismo helper para corregir lo
 * que pinta cuando el valor sale de rango.
 */
export const MAX_UNIT_PRICE = 999.99;

export const clampUnitPrice = (value) => {
  const parsed = Number(value) || 0;
  if (parsed < 0) return 0;
  if (parsed > MAX_UNIT_PRICE) return MAX_UNIT_PRICE;
  return parsed;
};

export const clampQuantity = (value) => {
  const parsed = Number(value) || 0;
  return parsed < 0 ? 0 : parsed;
};

/**
 * Subtotal y total de un renglon de metodo de pago. El total lleva el IVA de
 * la tasa a la que pertenece el renglon.
 */
export const calcPaymentAmounts = ({ unitPrice, quantity, taxRate } = {}) => {
  // Mismo contrato que el reducer: negativos se limitan a 0 (y el PU al
  // maximo), para que un -10 x 5 nunca produzca un subtotal de -50.
  const subtotal = clampUnitPrice(unitPrice) * clampQuantity(quantity);
  const total = subtotal + subtotal * ((Number(taxRate) || 0) / PERCENT);

  return {
    subtotal: truncateAmount(subtotal),
    total: truncateAmount(total),
  };
};

/**
 * Totales de una tasa a partir de sus metodos de pago.
 *
 * `averageUnitPrice` es el PU sin IVA que se pinta en la cabecera azul del
 * acordeon: promedio simple de los PU, no subtotal entre litros.
 */
export const calcTaxBreakdownTotals = (payments = {}) => {
  const rows = Object.values(payments || {});
  // Los metodos en cero (sin movimiento, completados por fillPaymentGroups)
  // no cuentan para el promedio: diluirian el PU con ceros que no son parte
  // real de la venta.
  const rowsWithData = rows.filter((payment) => (Number(payment?.quantity) || 0) > 0);

  const totals = rows.reduce(
    (acc, payment) => ({
      quantity: acc.quantity + (Number(payment?.quantity) || 0),
      subtotal: acc.subtotal + (Number(payment?.subtotal) || 0),
      total: acc.total + (Number(payment?.total) || 0),
    }),
    { quantity: 0, subtotal: 0, total: 0 },
  );

  const averageUnitPrice =
    rowsWithData.length > 0
      ? rowsWithData.reduce((sum, payment) => sum + (Number(payment?.unitPrice) || 0), 0) /
        rowsWithData.length
      : 0;

  return { ...totals, averageUnitPrice };
};

/**
 * Totales de un permiso a partir de sus tasas: son los que se muestran en el
 * renglon del permiso, junto a su etiqueta.
 */
export const calcCreLicenseTotals = (taxBreakdown = []) =>
  (taxBreakdown || []).reduce(
    (acc, tax) => ({
      quantity: acc.quantity + (Number(tax?.quantity) || 0),
      total: acc.total + (Number(tax?.total) || 0),
    }),
    { quantity: 0, total: 0 },
  );

/**
 * Total Acumulado a Facturar: lo unico del pie que no manda el backend,
 * porque cambia cada vez que el usuario edita un renglon en manual.
 */
export const calcTotalToBill = (pendingByCreLicense = []) =>
  (pendingByCreLicense || []).reduce(
    (acc, license) => ({
      quantity: acc.quantity + (Number(license?.quantity) || 0),
      total: acc.total + (Number(license?.total) || 0),
    }),
    { quantity: 0, total: 0 },
  );

/**
 * Suma de cantidad, subtotal y total de las filas del modal de confirmacion
 * (una por permiso y metodo de pago), para el renglon TOTAL al pie.
 */
export const sumBreakdownRows = (rows = []) =>
  (rows || []).reduce(
    (acc, row) => ({
      quantity: acc.quantity + (Number(row?.quantity) || 0),
      subtotal: acc.subtotal + (Number(row?.subtotal) || 0),
      total: acc.total + (Number(row?.total) || 0),
    }),
    { quantity: 0, subtotal: 0, total: 0 },
  );

/**
 * Etiqueta del acordeon: permiso y codigo postal, ambos del backend.
 * Sin codigo postal se muestra el permiso solo, sin el separador colgando.
 */
export const buildCreLicenseLabel = ({ creLicense, postalCode } = {}) => {
  if (!creLicense) return "";
  return postalCode ? `${creLicense} - ${postalCode}` : `${creLicense}`;
};

/**
 * Llave de un renglon dentro de la vista. Con varios permisos en pantalla la
 * tasa sola ya no basta: se necesita la terna completa.
 */
export const paymentFieldKey = (creLicense, taxRate, paymentKey) =>
  `${creLicense}_${taxRate}_${paymentKey}`;

/**
 * Facturacion Pendiente = Venta GN4 - Facturacion GN4 - Facturacion No GN4.
 * Formula verificada contra la respuesta acordada con Dionisio:
 * 1054 - 41641.35 - 0 = -40587.35, y 100 - 3765.56 - 0 = -3665.56.
 *
 * El backend la manda calculada, pero se recalcula aqui cuando el usuario
 * captura la Facturacion No GN4 a mano.
 */
export const calcPendingToBill = (sales = {}, billsGn4 = {}, billsNoGn4 = {}) => ({
  quantity:
    (Number(sales?.quantity) || 0) -
    (Number(billsGn4?.quantity) || 0) -
    (Number(billsNoGn4?.quantity) || 0),
  total:
    (Number(sales?.total) || 0) -
    (Number(billsGn4?.total) || 0) -
    (Number(billsNoGn4?.total) || 0),
});

/**
 * Arma el payload de timbrado con el formato que espera el backend
 * (creLicense -> taxBreakdown -> payments[]).
 *
 * El backend rechaza cualquier arreglo vacio con "El arreglo no puede estar
 * vacio" (validador generico, ver COM/API/src/validator.ts en crm_backend).
 * Se omite la tasa que se quede sin metodos de pago, y el permiso que se
 * quede sin tasas, en vez de mandarlos en blanco.
 */
export const buildCreLicenseBreakdown = (pendingByCreLicense = [], selectedPayments = {}) =>
  pendingByCreLicense
    .map((license) => {
      const taxBreakdown = (license.taxBreakdown || [])
        .map((taxGroup) => {
          const taxRate = taxGroup.taxRate;

          // Los metodos en cero que fillPaymentGroups completo no se mandan
          // salvo que el usuario los haya capturado en Manual (ahi ya traen
          // cantidad real). Un metodo con availability (ya hay una factura en
          // cola que se traslapa, CRM-3109) solo es una advertencia visual:
          // el usuario puede editarlo y mandarlo a timbrar de todos modos.
          const payments = Object.entries(taxGroup.payments || {})
            .filter(([, payment]) => (Number(payment?.quantity) || 0) > 0)
            // Sin idPayment (catalogo /payments aun sin cargar al normalizar)
            // el backend recibiria undefined: mejor no mandar el renglon.
            .filter(([, payment]) => payment?.idPayment != null)
            .map(([methodKey, payment]) => ({
              idPayment: payment.idPayment,
              isManualInvoice: !!selectedPayments[
                paymentFieldKey(license.creLicense, taxRate, methodKey)
              ],
              unitPrice: payment.unitPrice,
              quantity: payment.quantity,
              // El importe se redondea a centavos (no se trunca: truncar
              // quita un centavo por error de punto flotante, ej. 1.15 ->
              // 1.14). Number(x) || 0 evita NaN/null con un pago sin
              // subtotal/total.
              subtotal: Number((Number(payment.subtotal) || 0).toFixed(2)),
              total: Number((Number(payment.total) || 0).toFixed(2)),
              ids: payment.ids || [],
            }));

          if (payments.length === 0) return null;
          return { taxRate, payments };
        })
        .filter(Boolean);

      if (taxBreakdown.length === 0) return null;
      return { creLicense: license.creLicense, taxBreakdown };
    })
    .filter(Boolean);

/**
 * Filas planas para el modal de confirmacion: una por permiso y metodo de
 * pago, con la etiqueta del permiso y si el renglon quedo marcado Manual.
 * Solo se listan los metodos con cantidad real, igual que en el payload que
 * se manda a timbrar.
 */
export const buildBreakdownModalRows = (pendingByCreLicense = [], selectedPayments = {}) => {
  const rows = [];

  pendingByCreLicense.forEach((license) => {
    const { creLicense, postalCode } = license;

    (license.taxBreakdown || []).forEach((taxGroup) => {
      const taxRate = taxGroup.taxRate;

      // Igual que el payload de timbrado: los metodos en cero que nadie
      // capturo no aparecen en el resumen de confirmacion.
      Object.entries(taxGroup.payments || {})
        .filter(([, payment]) => (Number(payment?.quantity) || 0) > 0)
        .forEach(([methodKey, payment]) => {
          rows.push({
            ...payment,
            taxRate,
            creLicense,
            creLicenseLabel: buildCreLicenseLabel({ creLicense, postalCode }),
            isManualInvoice: !!selectedPayments[
              paymentFieldKey(creLicense, taxRate, methodKey)
            ],
          });
        });
    });
  });

  return rows;
};

/**
 * Un solo permiso abierto a la vez: si ya estaba abierto se cierra, si no,
 * se abre y cierra cualquier otro.
 */
export const toggleSingleSelection = (current, key) =>
  current === key ? null : key;

/**
 * Marca o desmarca una llave dentro del mapa de pagos seleccionados
 * (renglones capturados en Manual).
 */
export const togglePaymentSelection = (selectedPayments = {}, key) => {
  const next = { ...selectedPayments };
  if (next[key]) delete next[key];
  else next[key] = true;
  return next;
};

/**
 * Facturacion No GN4 en Manual: se enciende con el valor que trajo el
 * backend (para no partir de cero) y se apaga a null, que es la señal de
 * "usa lo que diga el backend" en vez de lo capturado a mano.
 */
export const nextManualNoGn4 = (currentManual, billsNoGn4) => {
  if (currentManual) return null;
  const seed = billsNoGn4 || { total: 0, quantity: 0 };
  // Se siembra como texto, no como numero: si se guardara ya convertido,
  // el input mostraria "12" en vez de "12." en cuanto el usuario tecleara el
  // punto, porque cada re-render pintaria el numero parseado, no lo escrito.
  return { total: String(seed.total ?? 0), quantity: String(seed.quantity ?? 0) };
};

/**
 * Lo que se deja escribir en los campos de Facturacion No GN4: solo digitos
 * y punto -nunca signo. Una facturacion manual no puede ser negativa, asi
 * que el "-" ni siquiera se deja teclear (antes se preservaba y colaba
 * valores negativos hasta el calculo de Facturacion Pendiente).
 *
 * Devuelve texto, no un numero ya parseado -Number("12.") es valido y
 * volveria a pintar "12", perdiendo el punto que el usuario acaba de teclear
 * y dejandolo sin poder seguir escribiendo el decimal. Quien consuma el
 * valor (calcPendingToBill, el payload de timbrado) hace Number() por su
 * cuenta, como ya hace con el resto de los importes de este modulo.
 */
export const parseNoGn4FieldValue = (rawValue) =>
  String(rawValue ?? "")
    .replace(/[^\d.]/g, "")
    .replace(/(\..*)\./g, "$1");

/**
 * Los 5 metodos de pago de contado que la vista siempre muestra por tasa,
 * esten o no en la respuesta del backend. La llave coincide con PaymentGroup
 * (CRM-3109); el nombre es el que trae el catalogo de /payments, con el que
 * se resuelve el idPayment real para poder facturar por ese metodo.
 */
export const PAYMENT_GROUPS = [
  { key: "cash", name: "EFECTIVO" },
  { key: "check", name: "CHEQUE" },
  { key: "transfer", name: "TRANSFERENCIA ELECTRONICA DE FONDOS" },
  { key: "creditCard", name: "TARJETA DE CREDITO" },
  { key: "debitCard", name: "TARJETA DE DEBITO" },
];

/**
 * Cruza por nombre contra el catalogo de /payments: los ids cambian entre
 * ambientes, el nombre es lo unico estable.
 */
// Quita acentos para comparar nombres de forma de pago: si el catalogo de
// /payments trae variantes acentuadas ("CRÉDITO", "ELECTRÓNICA") en algun
// ambiente, no deben perderse contra PAYMENT_GROUPS (sin acentos) y dejar el
// idPayment en undefined.
const normalizePaymentName = (name) =>
  `${name || ""}`.trim().toUpperCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export const resolvePaymentIdByName = (name, paymentTypes = []) =>
  paymentTypes.find(
    (type) => normalizePaymentName(type?.name) === normalizePaymentName(name),
  )?.id;

const emptyPayment = (idPayment, name) => ({
  idPayment,
  name,
  quantity: 0,
  subtotal: 0,
  unitPrice: 0,
  total: 0,
  ids: [],
  availability: null,
});

/**
 * Reconstruye el objeto de metodos de pago con los 5 grupos siempre
 * presentes, en el mismo orden (las llaves desconocidas se agregan despues). El que trajo data del backend se conserva
 * tal cual; el que no, se completa en ceros para que el usuario pueda
 * activarlo en Manual y capturarlo aunque no haya tenido movimiento.
 */
export const fillPaymentGroups = (payments = {}, paymentTypes = []) => {
  const known = PAYMENT_GROUPS.reduce((acc, group) => {
    acc[group.key] =
      payments[group.key] ||
      emptyPayment(resolvePaymentIdByName(group.name, paymentTypes), group.name);
    return acc;
  }, {});

  // Las llaves fuera de los 5 grupos se conservan: descartarlas perderia
  // ventas que el backend si reporta.
  const extras = Object.fromEntries(
    Object.entries(payments || {}).filter(([key]) => !(key in known)),
  );
  return { ...known, ...extras };
};

/**
 * True si al menos un metodo de pago tiene datos reales. Con
 * fillPaymentGroups el objeto de pagos nunca esta realmente vacio -siempre
 * trae los 5 metodos-, asi que "vacio" para efectos de negocio significa
 * que ninguno tiene cantidad mayor a cero.
 */
export const hasPaymentsWithData = (payments = {}) =>
  Object.values(payments || {}).some((payment) => (Number(payment?.quantity) || 0) > 0);

/**
 * Snapshot de PU y litros tal como llegaron del backend, por renglon
 * (permiso + tasa + metodo de pago). Se construye una sola vez por busqueda
 * y sirve para restaurar un renglon al desmarcar Manual -sin esto, si el
 * componente que edita se desmonta y remonta (el Collapse de un permiso), el
 * "original" se recalcularia leyendo el store ya editado en vez del valor
 * real que trajo el backend.
 */
export const buildOriginalPayments = (pendingByCreLicense = []) => {
  const originals = {};

  pendingByCreLicense.forEach((license) => {
    (license.taxBreakdown || []).forEach((tax) => {
      Object.entries(tax.payments || {}).forEach(([key, payment]) => {
        originals[paymentFieldKey(license.creLicense, tax.taxRate, key)] = {
          unitPrice: payment?.unitPrice ?? 0,
          quantity: payment?.quantity ?? 0,
        };
      });
    });
  });

  return originals;
};

/**
 * Las unicas dos tasas de IVA que maneja el sistema (documentadas en
 * CRM-3109: `taxRate: number // 16 | 8`).
 */
export const DEFAULT_TAX_RATES = [16, 8];

/**
 * Cuando un permiso llega con `taxBreakdown: []` -sin actividad en el
 * periodo- se sintetizan las dos tasas estandar, cada una con los 5 metodos
 * de pago en cero, para que igual se pueda desplegar y capturar en Manual.
 */
export const buildDefaultTaxBreakdown = (paymentTypes = []) =>
  DEFAULT_TAX_RATES.map((taxRate) => {
    const payments = fillPaymentGroups({}, paymentTypes);
    return { taxRate, ...calcTaxBreakdownTotals(payments), payments };
  });

/**
 * Nombre legible de un metodo de pago, cruzado por id contra el catalogo de
 * /payments.
 */
export const resolvePaymentNameById = (idPayment, paymentTypes = []) =>
  paymentTypes.find((type) => type.id === idPayment)?.name;

/**
 * El SAT no acepta partidas por debajo de 1 litro (CRM-3022). A diferencia
 * del flujo de un solo permiso donde nacio esa regla, aqui fillPaymentGroups
 * siempre completa los 5 metodos de pago con cantidad en cero para poder
 * capturarlos en Manual -por eso se descartan antes de validar: un metodo
 * sin tocar (0 Lt) no es una partida invalida, solo esta vacio.
 */
export const buildInvalidQuantityRows = (pendingByCreLicense = []) =>
  pendingByCreLicense.flatMap((license) =>
    (license.taxBreakdown || []).flatMap((tax) =>
      Object.values(tax.payments || {})
        .filter((payment) => (Number(payment?.quantity) || 0) > 0)
        .filter((payment) => isInvalidQuantity(payment.quantity))
        .map((payment) => ({
          creLicense: license.creLicense,
          taxRate: tax.taxRate,
          name: payment.name,
          quantity: Number(payment.quantity) || 0,
        })),
    ),
  );

/**
 * Mensaje de snackbar cuando hay partidas por debajo del minimo timbrable,
 * con el permiso, la tasa y el metodo de cada una para que el usuario las
 * ubique sin adivinar (con varios permisos en pantalla, la tasa sola ya no
 * alcanza).
 */
export const buildInvalidQuantityMessage = (invalidRows = []) => {
  const detail = invalidRows
    .map(({ creLicense, taxRate, name }) => `${creLicense} · ${taxRate}% - ${name}`)
    .join(", ");

  return `No se puede timbrar: las siguientes partidas tienen menos de ${MIN_INVOICE_QUANTITY} Lt: ${detail}. Corrige las cantidades para timbrar todo.`;
};
